/* ============================================================================
   BO CLASSIFICATION (BO UI Standard §2) — every screen picks exactly ONE class.
   ----------------------------------------------------------------------------
   THE single source of truth for a screen's class. "Set it once" here; the
   List / Detail / toolbar / stepper / checklist follow from it, so nobody
   guesses (§2).  boClassOf(navId) is the lookup used across the shell.

     A · Reference / Lookup     — tenant-shared · NO stepper · flat form ≤8 fields, inline-edit
     B · Master Data            — tenant-shared · NO stepper · tabbed child grids
     C · Transaction            — division      · STEPPER   · stepper + section grids
     D · Workflow / Review      — division      · STEPPER   · stepper + review marks
     E · Read-only / Analytics  — either        · NO stepper · grid/cards only, no New/Edit/Delete
     F · Config / Admin panel   — tenant/admin  · NO stepper · bespoke control body + ONE global Save

   A screen that can't be placed in one class is a design smell — split it (§2).
   ============================================================================ */

export type BoClass = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

export interface BoClassMeta {
  name: string;
  hasStepper: boolean;   // Class C + D only
  scope: 'tenant' | 'division' | 'either' | 'admin';
  detailShape: string;
}

export const BO_CLASS_META: Record<BoClass, BoClassMeta> = {
  A: { name: 'Reference / Lookup',    hasStepper: false, scope: 'tenant',   detailShape: 'Flat form, ≤8 fields, inline-edit allowed' },
  B: { name: 'Master Data',           hasStepper: false, scope: 'tenant',   detailShape: 'Tabbed, child grids' },
  C: { name: 'Transaction',           hasStepper: true,  scope: 'division', detailShape: 'Stepper + section grids' },
  D: { name: 'Workflow / Review',     hasStepper: true,  scope: 'division', detailShape: 'Stepper + review marks' },
  E: { name: 'Read-only / Analytics', hasStepper: false, scope: 'either',   detailShape: 'Grid / cards only — no New/Edit/Delete' },
  F: { name: 'Config / Admin panel',  hasStepper: false, scope: 'admin',    detailShape: 'Bespoke control body (toggles/pickers) + ONE global Save' },
};

/** Explicit per-screen class. Keyed by navData/nav-registry id. Screens not
 *  listed fall through to the prefix rules in classForPrefix(), then default. */
const EXPLICIT: Record<string, BoClass> = {
  // ── C · Transaction (stepper + section grids) ──────────────────────────
  sampreg: 'C', fielddata: 'C', sampreceipt: 'C', samptransfer: 'C', fieldintake: 'C', samplecancel: 'C',sampaloc: 'C', sampphcheck: 'C',
  suboutreg: 'C', subouttest: 'C', schedtask: 'C', taskcal: 'C',
  preplqueue: 'C', prepbatches: 'C', analysisq: 'C', qcbatches: 'C',
  custreport: 'C', batchreport: 'C', eddgen: 'C', morlist: 'C', morreport: 'C',
  msr: 'C', // Monthly Summary Repository — Query→Load→Save reporting BO (CRUD, not read-only)
  'edi-import': 'C',

  // ── D · Workflow / Review (stepper + review marks) ─────────────────────
  sdmsval: 'D', sdmsappr: 'D', rval: 'D', rappr: 'D',
  repval: 'D', repappr: 'D', repdel: 'D', morpapp: 'D', morpdel: 'D',

  // ── F · Config / Admin panel (bespoke body + ONE Save) ─────────────────
  formsetup: 'F', defaultsettings: 'F', appconfig: 'F', audittrailcfg: 'F',
  emailalerts: 'F', suboutnotif: 'F', 'mor-template': 'F', custreportbuilder: 'F',
  regcompliance: 'F', mydetails: 'F', 'sec-roles': 'F', 'sec-mac': 'F',
  'sm-navigation': 'F', clientversion: 'F', alpacaappconfig: 'F', screenautolock: 'F',
  hotkeysconfig: 'F',
  // Settings → Default Settings → Report ID Format + Date Filter (basic ports 2026-09-29,
  // bespoke config panels with one Save — Date Filter's body is a per-view grid).
  reportidformat: 'F', datefilter: 'F',
  dashdesigner: 'F', dashpreview: 'F',
  // System ▸ Dashboard — the list whose detail is the embedded DevExpress designer
  // (a bespoke config body with its own Save), ADR-0016.
  dashboards: 'F',

  // ── B · Master Data (tabbed, child grids) ──────────────────────────────
  'set-methods': 'B', 'set-testcode': 'B', 'adc-chain': 'B',
  // Sample Field Configuration — tabbed field-config detail (VisualMatrix +
  // SamplingFieldConfiguration child grids), so B not the set-* default 'A'.
  'set-samplefieldconfig': 'B',
  // EDD Builder — full List → Detail CRUD (editable header via the shared
  // SettingsDetailPage + a "Query Details" child grid whose rows open the EDDQuery
  // Builder editor dialog). B (not the earlier read-only 'E') so crud is defined and
  // ModuleListView shows New/Edit/Delete + the Detail View + toggle.
  'set-eddbuilder': 'B',
  // Formula Library (Settings ▸ SDMS) — flat CRUD form, ≤10 fields, shared with
  // SDMS templates (2026-07-28). Same shape as set-holidays/set-methods; explicit
  // here since it doesn't match a prefix rule (would default to 'E' read-only).
  formulalib: 'A',
  // Data Package Review Checklist (Settings) — flat CRUD master over
  // dbo.DataPackageCheckList (CheckPoint + Department + Test + QC roles + Default
  // + Sort + Comment). No prefix match, so classify explicitly (else it defaults
  // to 'E' read-only and loses the New/Edit/Delete + checkbox/edit columns).
  'data-package-checklist': 'A',
  // Data Package Test View — a READ-ONLY list over dbo.DataPackage (no moduleCrud);
  // force 'E' so it does NOT get the set-* prefix default 'A' New/Edit/Delete toolbar
  // (2026-09-29). DB Connection (set-dbconnection) keeps the set-* 'A' default (editable).
  'set-datapackage-testview': 'E',
  // Settings ▸ E-mail ▸ Report Recipient View — READ-ONLY filtered Customer list (no
  // moduleCrud); force 'E' so it doesn't get the set-* 'A' New/Edit/Delete toolbar.
  // Email + Email Content Template keep the set-* 'A' default (editable). 2026-09-30.
  'set-report-recipient-view': 'E',
  // Settings ▸ Field Tests — READ-ONLY view of dbo.TestMethod (IsFieldTest=1); force 'E'
  // (full TestMethod editing lives in Test Code). 2026-09-30.
  'set-field-tests': 'E',
  // Settings ▸ Flutter (2026-09-30). Flutter Default Settings is a singleton config
  // panel (one Save) → 'F'. Field App Table is now full CRUD → 'A' (flat master, owner
  // request 2026-10-02). set-flutter-template-builder was a read-only orphan duplicate
  // of set-flutter-field-template-builder; wired up with the SAME full CRUD → 'A' at
  // owner request 2026-10-16 (a second editor over the same SDATemplate data).
  'set-flutter-defaultsettings': 'F',
  'set-flutter-fieldapptable': 'A',
  'set-flutter-template-builder': 'A',
  // Field Template Builder is now header CRUD → 'A' (owner request 2026-10-02).
  'set-flutter-field-template-builder': 'A',
  // Settings ▸ EDD Builder + METRC (2026-09-30) — READ-ONLY list stubs → 'E'. The EDD
  // builder UI and the METRC live-API integration await the legacy source (§18.11b).
  'set-metrc-incoming': 'E',
  // Retired Document — READ-ONLY filtered view of dbo.Manual (IsRetire=1). Class 'A'
  // (not 'E') so its moduleCrud loads (a class-E screen drops MODULE_CRUD entirely,
  // boClass line ~568); the read-only-ness comes from moduleCrud detailReadOnly +
  // detailHideEdit, giving a read-only list AND a row-click read-only detail
  // (legacy Manual DetailView) with an Attachments grid. 2026-09-29.
  'set-retireddocument': 'A',
  // Items ▸ "Sub Item" child grid's own New/Edit dialog (moduleCrud "sub-items") — a
  // flat 4-field CRUD popup (Sub Item / Concentration / Remark / hidden LinkToItemId),
  // no top-level nav entry of its own, so no prefix rule matches. Explicit here (else
  // it defaults to 'E' read-only and its New/Edit dialog loses its edit affordances).
  'sub-items': 'A',
  // Pre-existing gap found while fixing the one above (same oversight, not introduced
  // today) — three more child-row CRUD dialogs with no nav entry / prefix match, so
  // they silently defaulted to 'E' read-only too. Same fix, same reasoning.
  'hc-attachment-row': 'A',
  'hc-versionlog-row': 'A',
  'pln-permit-limit-row': 'A',
  // Vendors ▸ "Evaluation" / "Upload" child grids' own New/Edit dialogs (2026-09-23).
  // Same shape and same reasoning as 'sub-items' above: child-row CRUD popups with no
  // nav entry and no prefix match, so without these they default to 'E' read-only.
  'vendor-evaluation-row': 'A',
  'vendor-upload-row': 'A',
  // Settings ▸ Permit Library New ▸ Permit Type — flat CRUD lookup over
  // dbo.PermitTypes. The 'pln-' prefix matches no rule in classForPrefix(), so
  // without this it would default to 'E' (read-only) and lose New/Edit/Delete.
  'pln-permit-library': 'A',
  'pln-permit-type': 'A',
  // Limit Type is nav-only today (no data source); classed 'A' with its
  // siblings so it behaves as a lookup the moment a table is wired in.
  'pln-limit-type': 'A',
  // Operators is a STATIC read-only reference list (a ModuleRegistry VALUES clause
  // — no table/controller/CRUD), so it is read-only BY ITS moduleCrud config
  // (detailReadOnly + detailHideEdit, no New/Edit/Delete). It is classed 'A' — NOT
  // 'E' — on purpose: Class E forces crud=undefined, which strips the whole crud
  // path INCLUDING the List/Detail toggle the owner asked for (2026-09-28). The
  // read-only-ness comes from the config, the toggle + read-only detail from the
  // class enabling crud.
  'pln-operators': 'A',
  'acc-paymentterms': 'B',
  // Glossary + Message Editor carry a nested "Translations" child-collection grid
  // (sanctioned Class B popup exception to §4 — see GlossaryDetailDialog /
  // MessageEditorDetailDialog headers), so they are B, not the sm-* default 'F'.
  'sm-message-editor': 'B', 'sm-glossary': 'B',
  // State/Country are master-data lookups with child-collection grids (cities under
  // a state, states under a country — StateCitiesPanel / CountryStatesPanel), so they
  // are B, NOT the sm-* prefix default 'F' (which is for admin/config panels).
  'sm-state': 'B', 'sm-country': 'B',

  // ── E · Read-only / Analytics (grid/cards, no CUD) ─────────────────────
  home: 'E', execdash: 'E', avainsight: 'E', histres: 'E', jobprog: 'E',
  testprog: 'E', servermon: 'E', 'servermon-history': 'E', useractivity: 'E',
  exceptionlog: 'E',
  trendanalysis: 'E', mdlstudies: 'E', reptrack: 'E', restrack: 'E',
  icm01: 'E', icm02: 'E', icm03: 'E', icm05: 'E',

  // ── A · Reference / Lookup ─────────────────────────────────────────────
  'acc-region': 'A', 'dwrc-cats': 'A', 'dwrc-subcats': 'A',
  'msr-water-system': 'A', 'msr-data-import-config': 'F',
  // Document Management ▸ Qualifier (legacy dbo.Qualifiers) — reference lookup
  // used by results/SDMS. No prefix match, so classify explicitly (else it
  // defaults to 'E' read-only and loses New/Edit/Delete + the checkbox/edit
  // columns). Full-page detail with the Sample/QC/Surrogate automation sections.
  qualifier: 'A',
};

/** Prefix → class for the big families (audit views, Data Center grids,
 *  dashboards, settings lookups, notification-centre config). */
function classForPrefix(id: string): BoClass | null {
  if (id.startsWith('at-'))  return 'E';   // Audit Trail Log views — read-only
  if (id.startsWith('dc-'))  return 'E';   // Data Center grids — read-only
  if (id.startsWith('dc2-')) return 'E';   // Data Center v2 grids — read-only
  if (id.startsWith('pa-'))  return 'E';   // Performance-Analytics dashboards
  if (id.startsWith('nc-'))  return 'F';   // Notification Center config
  if (id.startsWith('sm-'))  return 'F';   // Security / user management (admin)
  if (id.startsWith('sec-')) return 'F';   // User & Security admin
  if (id.startsWith('edi-')) return 'A';   // External-data permit libraries — lookups
  if (id.startsWith('msr-')) return 'A';   // MSR reference data
  if (id.startsWith('acc-')) return 'A';   // Accounting master/lookup
  if (id.startsWith('set-')) return 'A';   // Settings ▸ Utility lookups
  return null;
}

/** Nav-structure ids that are NOT screens (groups, sub-groups, separators). */
function isNonScreen(id: string): boolean {
  return id.startsWith('grp-') || id.startsWith('sg-') || id.startsWith('sep');
}

/**
 * The class for a screen id. Read this ONCE per screen; List/Detail/toolbar/
 * stepper/checklist follow from it (§2). Returns null for nav groups/separators.
 * Unknown leaf screens default to 'E' (read-only) — the safest class (no
 * mutating actions) until the screen is explicitly classified here.
 */
export function boClassOf(id: string | undefined | null): BoClass | null {
  if (!id) return null;
  if (isNonScreen(id)) return null;
  if (EXPLICIT[id]) return EXPLICIT[id];
  const byPrefix = classForPrefix(id);
  if (byPrefix) return byPrefix;
  return 'E';
}

/** Does this screen get the Dynamic-Island workflow stepper? (Class C + D only, §2/§5) */
export function hasStepper(id: string | undefined | null): boolean {
  const c = boClassOf(id);
  return c === 'C' || c === 'D';
}

/** Is this a read-only screen (no New/Edit/Delete on the toolbar)? (Class E, §2) */
export function isReadOnly(id: string | undefined | null): boolean {
  return boClassOf(id) === 'E';
}
